export const DAY = 86_400;
export const UNBONDING_SECONDS = 14 * DAY;
export const APY_BPS = 1_000;
export const MIN_PENALTY_BPS = 1_000;

export const TERMS = {
  6: { days: 180, initialPenaltyBps: 2_500 },
  9: { days: 270, initialPenaltyBps: 2_500 },
  12: { days: 365, initialPenaltyBps: 2_500 },
} as const;

export type TermMonths = keyof typeof TERMS;

export function nonnegativeFinite(value: number) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function sanitizeAmountInput(value: string, maxFractionDigits?: number) {
  const sanitized = value.trim();
  if (!/^(?:\d+\.?\d*|\.\d+)$/.test(sanitized) || !Number.isFinite(Number(sanitized))) return "0";
  const normalized = sanitized.startsWith(".") ? `0${sanitized}` : sanitized;
  if (maxFractionDigits === undefined || !normalized.includes(".")) return normalized;
  const [whole, fraction = ""] = normalized.split(".");
  return `${whole}.${fraction.slice(0, Math.max(0, maxFractionDigits))}`;
}

export function decimalRawAmount(value: string, decimals: number) {
  if (!/^\d+(?:\.\d*)?$/.test(value)) return 0n;
  const [whole, fraction = ""] = value.split(".");
  const scale = 10n ** BigInt(decimals);
  return BigInt(whole) * scale + BigInt(fraction.slice(0, decimals).padEnd(decimals, "0"));
}

export function assetRawAmount(value: string) {
  return decimalRawAmount(value, 9);
}

export function decimalAmount(raw: bigint, decimals: number) {
  const scale = 10n ** BigInt(decimals);
  const whole = raw / scale;
  const fraction = (raw % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function assetAmount(raw: bigint) {
  return decimalAmount(raw, 9);
}

export function earnAmount(raw: bigint) {
  return assetAmount(raw - raw % 10_000_000n);
}

export function balancePresets(raw: bigint) {
  return [["25%", earnAmount(raw / 4n)], ["50%", earnAmount(raw / 2n)], ["Max", earnAmount(raw)]] as const;
}

export function yieldUsdc(openingValueUsdc: number, term: TermMonths) {
  return nonnegativeFinite(nonnegativeFinite(openingValueUsdc) * (APY_BPS / 10_000) * (TERMS[term].days / 365));
}

export function penaltyBps(term: TermMonths, openedAt: number, maturity: number, now: number) {
  if (now >= maturity) return 0;
  const finalDay = maturity - DAY;
  if (now >= finalDay) return MIN_PENALTY_BPS;
  const initial = TERMS[term].initialPenaltyBps;
  const decayDuration = finalDay - openedAt;
  if (decayDuration <= 0 || now <= openedAt) return initial;
  return MIN_PENALTY_BPS + Math.ceil((initial - MIN_PENALTY_BPS) * (finalDay - now) / decayDuration);
}

export function marketFee(amount: bigint) {
  return (amount * 10n + 9_999n) / 10_000n;
}

export function projectedBondValue(amount: number, openingPrice: number, promisedUsdc: number) {
  return nonnegativeFinite(nonnegativeFinite(amount) * nonnegativeFinite(openingPrice) + nonnegativeFinite(promisedUsdc));
}

export function rewardBalances(value: string, status: string) {
  const reward = decimalRawAmount(value, 6);
  return {
    claimable: status === "matured" ? reward : 0n,
    claimed: status === "claimed" ? reward : 0n,
  };
}

export function hasMeaningfulReward(value: string) {
  return decimalRawAmount(value, 6) >= 100_000n;
}
